'use client';

import React, { useState, useEffect } from 'react';
import { ArrowLeft, Clock, MessageCircle, Star } from 'lucide-react';
import { useChatContext } from '@/context/ChatContext';
import { useAuth } from '@/context/AuthContext';
import { useSite } from '@/context/SiteContext';
import { chatSubmitCSAT, adminChatSaveSettings } from '@/lib/api-client';
import MessageThread from './MessageThread';
import ChatInput from './ChatInput';
import { useTimezone } from '@/hooks/useTimezone';

type View = 'chat' | 'history';

/**
 * The support live-chat experience (the former standalone ChatPanel) WITHOUT
 * its own floating shell, header, drag, resize, or close button. It fills the
 * unified AI/Support popup's body when Support mode is active. Closing the
 * whole popup is delegated to the host via `onClose`.
 */
export default function SupportChatBody({ onClose }: { onClose: () => void }) {
  const tz = useTimezone();
  const {
    settings,
    activeConversation, messages, conversations,
    startConversation, sendMessage, selectConversation,
    sending, staffTyping, sendTyping, guestToken,
  } = useChatContext();
  const { user } = useAuth();
  const { siteName } = useSite();
  const [view, setView] = useState<View>('chat');
  const [guestEmail, setGuestEmail] = useState('');
  const [csatSubmitted, setCsatSubmitted] = useState(false);
  const [csatRating, setCsatRating] = useState(0);
  const [togglingOnline, setTogglingOnline] = useState(false);

  const isOnline = settings?.online ?? false;
  const isOffline = !isOnline;
  const isResolved = activeConversation?.status === 'resolved';
  const isStaffOrAdmin = user?.role === 'admin' || user?.role === 'staff';

  const handleToggleOnline = async () => {
    if (!isStaffOrAdmin || togglingOnline) return;
    setTogglingOnline(true);
    try { await adminChatSaveSettings({ online: !isOnline }); }
    catch { /* WS broadcast will update the state */ }
    finally { setTogglingOnline(false); }
  };

  // Request browser notification permission after first message
  useEffect(() => {
    if (messages.length === 1 && typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, [messages.length]);

  // Browser notification + tab-title flash when staff replies while hidden
  useEffect(() => {
    if (messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (last.senderType !== 'staff') return;
    if (document.hidden && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      new Notification(`${siteName} Support`, { body: last.body.slice(0, 100), icon: '/favicon.ico' });
    }
    if (document.hidden) {
      const orig = document.title;
      const flash = () => { document.title = document.title === orig ? `[1] New Message — ${siteName}` : orig; };
      const interval = setInterval(flash, 1500);
      const restore = () => { clearInterval(interval); document.title = orig; document.removeEventListener('visibilitychange', restore); };
      document.addEventListener('visibilitychange', restore);
      return () => { clearInterval(interval); document.title = orig; document.removeEventListener('visibilitychange', restore); };
    }
  }, [messages.length]);

  // If offline and no active conversation, show the contact form
  const showOfflineForm = isOffline && !activeConversation;

  const handleSend = async (body: string) => {
    // Require email for unauthenticated users before first message
    if (!user && !activeConversation && !guestEmail.includes('@')) return;
    if (activeConversation) {
      await sendMessage(body);
    } else {
      await startConversation(body, {
        deliveryMode: isOffline ? 'form' : 'live_chat',
        ...(guestEmail.includes('@') ? { guestEmail } : {}),
      });
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Status / nav sub-row */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-default">
        <div className="flex items-center gap-2">
          {view === 'history' && (
            <button onClick={() => setView('chat')} className="text-fg-muted hover:text-white" title="Back">
              <ArrowLeft size={14} />
            </button>
          )}
          {isStaffOrAdmin ? (
            <button
              onClick={handleToggleOnline}
              disabled={togglingOnline}
              className="flex items-center gap-1.5 hover:opacity-80 transition-opacity disabled:opacity-50"
              title={`Click to go ${isOnline ? 'offline' : 'online'}`}
            >
              <span className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-400' : 'bg-slate-500'}`} />
              <span className="text-[11px] text-fg-muted">{isOnline ? 'Online' : 'Offline'}</span>
            </button>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-400' : 'bg-slate-500'}`} />
              <span className="text-[11px] text-fg-muted">{isOnline ? 'Online' : 'Offline'}</span>
            </div>
          )}
        </div>
        {user && view !== 'history' && (
          <button onClick={() => setView('history')} className="p-1 text-fg-muted hover:text-white transition-colors" title="Chat history">
            <Clock size={15} />
          </button>
        )}
      </div>

      {/* History view */}
      {view === 'history' && (
        <div className="flex-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="flex items-center justify-center h-full text-sm text-fg-subtle">No previous conversations</div>
          ) : (
            <div className="divide-y divide-border-default/50">
              {conversations.map((conv) => (
                <button
                  key={conv.id}
                  onClick={() => { selectConversation(conv.id); setView('chat'); }}
                  className="w-full text-left px-4 py-3 hover:bg-card-hover-bg transition-colors"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-fg-muted">{new Date(conv.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: tz })}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      conv.status === 'resolved' ? 'bg-emerald-500/15 text-emerald-400' :
                      conv.status === 'closed' ? 'bg-slate-500/15 text-fg-muted' :
                      'bg-sky-500/15 text-sky-400'
                    }`}>{conv.status}</span>
                  </div>
                  <p className="text-sm text-slate-300 truncate">{conv.subject || 'Chat conversation'}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Chat view */}
      {view === 'chat' && !showOfflineForm && (
        <>
          {!activeConversation && settings?.welcomeMessage && (
            <div className="px-4 pt-3">
              <div className="bg-card-hover-bg rounded-xl px-3.5 py-2.5 text-sm text-slate-300">
                {settings.welcomeMessage}
              </div>
            </div>
          )}

          <MessageThread messages={messages} currentUserId={user?.id} />

          {staffTyping && (
            <div className="px-4 pb-1">
              <p className="text-[11px] text-sky-400 flex items-center gap-1.5 animate-pulse">
                <MessageCircle size={12} />
                Support is typing...
              </p>
            </div>
          )}
          {!staffTyping && activeConversation && activeConversation.status === 'open' && (
            <div className="px-4 pb-1">
              <p className="text-[11px] text-fg-subtle flex items-center gap-1.5">
                <MessageCircle size={12} />
                Waiting for support — you can keep typing.
              </p>
            </div>
          )}

          {!user && !activeConversation && (
            <div className="px-3 pb-2">
              <input
                type="email"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                placeholder="Your email (required)"
                className="w-full bg-surface border border-default rounded-lg px-2.5 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-sky-500/50"
              />
            </div>
          )}

          {isResolved && !csatSubmitted && !activeConversation?.csatRating && (
            <div className="px-3 pb-2">
              <div className="bg-surface border border-default rounded-lg px-3 py-3 text-center">
                <p className="text-xs text-fg-muted mb-2">How did we do?</p>
                <div className="flex justify-center gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} onClick={() => setCsatRating(n)} className="p-1 transition-colors">
                      <Star size={20} className={n <= csatRating ? 'text-amber-400 fill-amber-400' : 'text-slate-600'} />
                    </button>
                  ))}
                </div>
                {csatRating > 0 && (
                  <button
                    onClick={async () => {
                      await chatSubmitCSAT(activeConversation!.id, csatRating, undefined, guestToken);
                      setCsatSubmitted(true);
                    }}
                    className="text-xs text-sky-400 hover:text-sky-300"
                  >
                    Submit
                  </button>
                )}
                <button onClick={() => setCsatSubmitted(true)} className="text-xs text-slate-600 hover:text-fg-muted ml-3">Skip</button>
              </div>
            </div>
          )}
          {isResolved && (csatSubmitted || activeConversation?.csatRating) && (
            <div className="px-3 pb-2 text-center">
              <p className="text-xs text-fg-subtle">Chat ended. <button onClick={onClose} className="text-sky-400 hover:text-sky-300">Close</button></p>
            </div>
          )}

          {!isResolved && (
            <ChatInput
              onSend={handleSend}
              onTyping={sendTyping}
              disabled={sending || (!user && !activeConversation && !guestEmail.includes('@'))}
              placeholder={!user && !activeConversation && !guestEmail.includes('@') ? 'Enter your email above first...' : 'Type a message...'}
            />
          )}
        </>
      )}

      {/* Offline form */}
      {view === 'chat' && showOfflineForm && <OfflineForm />}
    </div>
  );
}

function OfflineForm() {
  const { startConversation, settings, sending } = useChatContext();
  const { user } = useAuth();
  const [name, setName] = useState(user?.displayName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [subject, setSubject] = useState('General');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || !email.trim()) return;
    await startConversation(message, { subject, deliveryMode: 'form' });
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-emerald-500/15 flex items-center justify-center mb-3">
          <MessageCircle size={22} className="text-emerald-400" />
        </div>
        <h4 className="text-sm font-semibold text-white mb-1.5">Message sent!</h4>
        <p className="text-xs text-fg-muted leading-relaxed">
          Thanks, we received your message. Expect a reply within 1 business day.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-3">
      <p className="text-xs text-fg-muted leading-relaxed">
        {settings?.offlineMessage ?? "We're currently offline. Leave us a message and we'll get back to you."}
      </p>
      {!user && (
        <>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="w-full bg-surface border border-default rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-sky-500/50"
          />
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email *"
            type="email"
            required
            className="w-full bg-surface border border-default rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-sky-500/50"
          />
        </>
      )}
      <select
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        className="w-full bg-surface border border-default rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500/50"
      >
        <option>General</option>
        <option>Billing</option>
        <option>Data Issue</option>
        <option>Feature Request</option>
        <option>Bug Report</option>
        <option>Other</option>
      </select>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Your message *"
        required
        maxLength={2000}
        rows={4}
        className="w-full bg-surface border border-default rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 resize-none focus:outline-none focus:border-sky-500/50"
      />
      <button
        type="submit"
        disabled={sending || !message.trim() || !email.trim()}
        className="w-full bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-sm font-medium py-2.5 rounded-lg transition-colors"
      >
        {sending ? 'Sending...' : 'Send Message'}
      </button>
    </form>
  );
}
